import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { Group } from '../../groups/entities/Group.js';
import { QuittungLine } from './QuittungLine.js';

/**
 * A receipt a payer can download: a Vorgang whose Soll was settled — or partly paid — by money that actually came in.
 * In the legacy mode (`Person.useLegacyQuittungen`) it is a single accounting entry instead.
 */
@Entity()
export class Quittung extends AdornisEntity {
  static override _class = 'Quittung';

  /** the settled Vorgang (`OpenItem._id`), or the accounting entry in the legacy mode */
  @Field(() => ID)
  _id!: string;

  /** when the last money came in */
  @Field(() => Date)
  date: Maybe<Date>;

  /** the money received, net of Rücklastschriften — never the charged amount */
  @Field(() => Int)
  amountInMinorUnit: Maybe<number>;

  /**
   * what is still open on the Vorgang: above 0 the receipt confirms a Teilzahlung, and `amountInMinorUnit` plus this
   * is the total it is part of. Empty in the legacy mode.
   */
  @Field(() => Int)
  openMinorUnit: Maybe<number>;

  /** what was paid for */
  @Field(() => String)
  text: Maybe<string>;

  @Field(() => String)
  processIdentifier: Maybe<string>;

  @Field(() => String)
  fiscalYear: Maybe<string>;

  @Field(() => String)
  iban: Maybe<string>;

  @Field(() => String)
  kontoinhaber: Maybe<string>;

  @Field(() => [ID], { default: v => v ?? [] })
  schoolIDs: Maybe<string[]>;

  /** loaded instead of stored names, so a renamed school is never stale here; query `schoolIDs` along with it */
  // @ts-expect-error _id as object id in mongo
  @DataLoadedField(() => [Group], ({ schoolIDs }) => schoolIDs)
  schools: Maybe<Group[]>;

  @Field(() => [QuittungLine], { default: v => v ?? [] })
  charges!: QuittungLine[];

  @Field(() => [QuittungLine], { default: v => v ?? [] })
  payments!: QuittungLine[];
}
